<template>
    <div class="kt-portlet kt-portlet--mobile" id="discounts-table" ref="element">
        <div class="kt-portlet__head kt-portlet__head--lg">
            <div class="kt-portlet__head-label">
                <DiscountsHeaderIcon />
                <h3 class="kt-portlet__head-title">{{ i18n("views.Discounts.ViewTitle") }}</h3>
            </div>

            <div class="kt-portlet__head-toolbar">
                <div class="kt-portlet__head-wrapper">
                    <a href="/discounts/new" class="btn btn-clean btn-icon-sm">
                        <i class="la la-plus"></i> {{ i18n("views.Discounts.NewDiscount") }}
                    </a>
                </div>
            </div>
        </div>

        <div class="kt-portlet__body tablefilter"></div>
        <div class="kt-portlet__body kt-portlet__body--fit">
            <div class="kt-datatable kt-datatable--default kt-datatable--brand kt-datatable--loaded"></div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { Factory } from "@/data/dao/Factory";
    import { DiscountsDao } from "@/data/dao/service/DiscountsDao";
    import type { IDiscount } from "@/data/model/Discount";
    import { DiscountStatus, DiscountType } from "@/data/model/Enums";
    import { DateTime } from "@/data/util/DateTime";
    import { Format } from "@/data/util/Format";
    import { i18n } from "@/portal/Trans";
    import { DataTable, type IDataTableConfig } from "@/portal/elements/DataTable";
    import { portalEvents } from "@/portal/events/portalEvents";
    import { translate } from "@/shared/localization/language";
    import { toMoneyWithSign } from "@/shared/utils/format";
    import { onMounted, ref } from "vue";

    import DiscountsHeaderIcon from "@/portal/components/discounts/DiscountsHeaderIcon.vue";

    const dao = Factory.getInstance<DiscountsDao>(DiscountsDao);

    const element = ref<HTMLDivElement>();
    let dataTable: DataTable<IDiscount>;

    function init() {
        const tableConfig = getTableConfig();

        dataTable = new DataTable<IDiscount>(
            $(element.value!).find("> .kt-portlet__body > .kt-datatable"),
            dao,
            tableConfig
        );

        dataTable.onClick(
            (m) => (location.href = "/discounts/" + m.id),
            (m) => window.open("/discounts/" + m.id)
        );

        portalEvents.OnLocationChanged.add(onLocationChanged);
    }

    onMounted(init);

    function onLocationChanged() {
        dataTable.loadData(true);
    }

    function getTableConfig(): IDataTableConfig<IDiscount> {
        const tableConfig: IDataTableConfig<IDiscount> = {
            columnMapping: [
                {
                    name: i18n("discount.fields.name"),
                    property: "name",
                    styles: { width: "10%" },
                    formatter: (m) => Format.htmlEscape(m.internalName ?? translate(m.name)),
                },
                {
                    name: i18n("discount.fields.type"),
                    property: "type",
                    formatter: (m) => i18n(`enums.DiscountType.${DiscountType[m.type]}`),
                    styles: { width: "10%" },
                },
                {
                    name: i18n("discount.fields.value"),
                    property: "value",
                    styles: { width: "10%" },
                    formatter: (m) => {
                        if (m.isOpenValue) {
                            return i18n("general.fields.open");
                        } else {
                            return m.type == DiscountType.Percentage
                                ? `${formatPercentage(m.percentageDiscountOptions?.value!)} %`
                                : `${toMoneyWithSign(m.absoluteDiscountOptions?.value!, true)}`;
                        }
                    },
                },
                {
                    name: i18n("general.timeFormat.start"),
                    property: "startDate",
                    styles: { width: "10%" },
                    formatter: (m) => {
                        return DateTime.toPreciseDateTimeString(m.applicability.startDateTime);
                    },
                },
                {
                    name: i18n("general.timeFormat.end"),
                    property: "endDate",
                    styles: { width: "10%" },
                    formatter: (m) => DateTime.toPreciseDateTimeString(m.applicability.endDateTime),
                },
                {
                    name: i18n("discount.fields.status"),
                    property: "status",
                    styles: { width: "10%" },
                    formatter: (m) => {
                        return i18n(`enums.DiscountStatus.${DiscountStatus[m.status]}`);
                    },
                },
                {
                    name: i18n("discount.fields.timesUsed"),
                    property: "timesUsed",
                    styles: { width: "10%" },
                    formatter: (m) => m.timesUsed,
                },
                {
                    name: i18n("discount.fields.totalDiscounted"),
                    property: "totalDiscounted",
                    styles: { width: "10%" },
                    formatter: (m) => toMoneyWithSign(m.totalDiscounted, true),
                },
                {
                    name: i18n("discount.fields.order"),
                    property: "order",
                    styles: { width: "10%" },
                    formatter: (m) => m.order,
                },
            ],
            locationDependend: true,
            defaultSortAsc: false,
        };

        return tableConfig;
    }

    const formatPercentage = (value: number) => {
        return value.toFixed(4).replace(/\.?0+$/, "");
    };
</script>
